/**
 * AudioWorklet processor for combustion engines, shipped as a string so it
 * loads from a Blob URL without a separate build entry.
 *
 * Model: a four-stroke cycle phase drives one ignition pulse per cylinder at
 * its firing offset. Each pulse excites a short noisy blast that runs through
 * the engine body (low-pass), two exhaust formants (band-pass) and a damped
 * pipe comb, then a load-dependent saturator. Intake roar, turbo spool with
 * blow-off, supercharger whine and overrun crackle are layered on top. Uneven
 * per-cylinder amplitudes and offsets give each layout its signature (V8
 * burble, flat-six howl, single-cylinder thump).
 */
export const ENGINE_PROCESSOR_NAME = 'nightshift-engine';

export const ENGINE_WORKLET_SOURCE = String.raw`
const TWO_PI = Math.PI * 2;

class Biquad {
  constructor() { this.b0 = 0; this.b1 = 0; this.b2 = 0; this.a1 = 0; this.a2 = 0; this.z1 = 0; this.z2 = 0; }
  set(kind, sr, frequency, q) {
    const f = Math.max(10, Math.min(frequency, sr * 0.45));
    const w = TWO_PI * f / sr;
    const s = Math.sin(w);
    const c = Math.cos(w);
    const alpha = s / (2 * Math.max(0.05, q));
    const a0 = 1 + alpha;
    if (kind === 0) { // low-pass
      this.b0 = (1 - c) / 2 / a0; this.b1 = (1 - c) / a0; this.b2 = this.b0;
    } else if (kind === 1) { // band-pass, constant peak gain
      this.b0 = alpha / a0; this.b1 = 0; this.b2 = -alpha / a0;
    } else { // high-pass
      this.b0 = (1 + c) / 2 / a0; this.b1 = -(1 + c) / a0; this.b2 = this.b0;
    }
    this.a1 = -2 * c / a0;
    this.a2 = (1 - alpha) / a0;
  }
  run(x) {
    const y = this.b0 * x + this.z1;
    this.z1 = this.b1 * x - this.a1 * y + this.z2;
    this.z2 = this.b2 * x - this.a2 * y;
    return y;
  }
}

const DEFAULT_PROFILE = {
  cylinders: 6, offsets: null, amplitudes: null, idleRpm: 900, redlineRpm: 7200,
  pulseDecayMs: 2.6, rasp: 0.45, bodyHz: 260, bodyGain: 1.1,
  formant1: 180, formant2: 900, formantQ: 3, formant1Gain: 1.4, formant2Gain: 0.6, formantTrack: 0.35,
  pipeMs: 3.2, pipeFeedback: 0.45, pipeDamp: 0.35,
  intake: 0.35, intakeHz: 900, turbo: 0, supercharger: 0, whineRatio: 3.2, crackle: 0.4,
  drive: 1.8, jitter: 0.08, gain: 1,
};

class EngineProcessor extends AudioWorkletProcessor {
  static get parameterDescriptors() {
    return [
      { name: 'rpm', defaultValue: 900, minValue: 0, maxValue: 20000, automationRate: 'k-rate' },
      { name: 'load', defaultValue: 0, minValue: 0, maxValue: 1, automationRate: 'k-rate' },
      { name: 'pitch', defaultValue: 1, minValue: 0.25, maxValue: 4, automationRate: 'k-rate' },
      { name: 'level', defaultValue: 0, minValue: 0, maxValue: 4, automationRate: 'k-rate' },
    ];
  }

  constructor(options) {
    super();
    this.sr = sampleRate;
    this.alive = true;
    this.seed = 0x2545f491 ^ Math.floor(Math.random() * 0x7fffffff);
    this.phase = Math.random();
    this.nextFire = 0;
    this.rpm = 900; this.load = 0; this.loadSlow = 0; this.level = 0; this.pitch = 1;
    this.env = 0; this.impulse = 0; this.popEnv = 0;
    this.spool = 0; this.bov = 0; this.whinePhase = 0; this.scPhase = 0;
    this.cutSamples = 0;
    this.dcX = 0; this.dcY = 0; this.preX = 0; this.preY = 0;
    this.preR = Math.exp(-TWO_PI * 38 / this.sr);
    this.delay = new Float32Array(4096); this.mask = 4095; this.write = 0; this.fbLow = 0;
    this.body = new Biquad(); this.f1 = new Biquad(); this.f2 = new Biquad();
    this.intakeBp = new Biquad(); this.bovHp = new Biquad(); this.popLp = new Biquad();
    this.bovHp.set(2, this.sr, 2600, 0.7);
    this.popLp.set(0, this.sr, 1400, 0.7);
    this.setProfile((options && options.processorOptions && options.processorOptions.profile) || {});
    this.port.onmessage = (event) => {
      const data = event.data || {};
      if (data.type === 'profile') this.setProfile(data.profile || {});
      else if (data.type === 'cut') this.cutSamples = Math.floor(Math.max(0, data.seconds || 0) * this.sr);
      else if (data.type === 'stop') this.alive = false;
    };
  }

  random() {
    let s = this.seed;
    s ^= s << 13; s ^= s >>> 17; s ^= s << 5;
    this.seed = s;
    return ((s >>> 0) / 4294967295) * 2 - 1;
  }

  setProfile(raw) {
    const p = Object.assign({}, DEFAULT_PROFILE, raw);
    const n = Math.max(1, Math.min(16, Math.round(p.cylinders)));
    p.cylinders = n;
    const offsets = [];
    const amplitudes = [];
    for (let i = 0; i < n; i += 1) {
      offsets.push(p.offsets && Number.isFinite(p.offsets[i]) ? ((p.offsets[i] % 1) + 1) % 1 : i / n);
      amplitudes.push(p.amplitudes && Number.isFinite(p.amplitudes[i]) ? p.amplitudes[i] : 1);
    }
    const order = offsets.map((offset, index) => [offset, amplitudes[index]]).sort((a, b) => a[0] - b[0]);
    p.offsets = order.map((entry) => entry[0]);
    p.amplitudes = order.map((entry) => entry[1]);
    this.p = p;
    this.envDecay = Math.exp(-1 / (Math.max(0.2, p.pulseDecayMs) * 0.001 * this.sr));
    this.drive = Math.max(0.4, p.drive);
    this.driveNorm = Math.tanh(this.drive);
    this.nextFire = 0;
    while (this.nextFire < n && p.offsets[this.nextFire] <= this.phase) this.nextFire += 1;
    this.body.set(0, this.sr, p.bodyHz, 0.9);
    this.intakeBp.set(1, this.sr, p.intakeHz, 0.8);
  }

  process(inputs, outputs, parameters) {
    const output = outputs[0];
    if (!output || output.length === 0) return this.alive;
    const out = output[0];
    const p = this.p;
    const sr = this.sr;
    const targetRpm = parameters.rpm[0];
    const targetLoad = parameters.load[0];
    const targetPitch = parameters.pitch[0];
    const targetLevel = parameters.level[0];
    const rpmNorm = Math.max(0, Math.min(1.15, (this.rpm - p.idleRpm) / Math.max(1, p.redlineRpm - p.idleRpm)));
    const track = 1 + p.formantTrack * (rpmNorm - 0.4);
    this.f1.set(1, sr, p.formant1 * track * this.pitch, p.formantQ);
    this.f2.set(1, sr, p.formant2 * track * this.pitch, p.formantQ * 0.8);
    this.intakeBp.set(1, sr, p.intakeHz * (0.7 + rpmNorm * 1.1) * this.pitch, 0.9);
    const pipeSamples = Math.max(8, Math.min(this.mask - 1, Math.round(p.pipeMs * 0.001 * sr / this.pitch)));
    const n = p.cylinders;
    const whineBase = 1800 + 7200 * this.spool;
    for (let i = 0; i < out.length; i += 1) {
      this.rpm += (targetRpm - this.rpm) * 0.0016;
      this.load += (targetLoad - this.load) * 0.004;
      this.loadSlow += (this.load - this.loadSlow) * 0.00035;
      this.pitch += (targetPitch - this.pitch) * 0.002;
      this.level += (targetLevel - this.level) * 0.002;
      let load = this.load;
      if (this.cutSamples > 0) { this.cutSamples -= 1; load = 0; }

      const step = (Math.max(0, this.rpm) / 120) / sr * this.pitch;
      this.phase += step;
      if (this.phase >= 1) { this.phase -= 1; this.nextFire = 0; }
      while (this.nextFire < n && p.offsets[this.nextFire] <= this.phase) {
        let amp = p.amplitudes[this.nextFire] * (0.32 + 0.68 * load) * (1 + p.jitter * this.random());
        if (load < 0.08 && rpmNorm > 0.3 && this.random() > 1 - p.crackle * 0.06) {
          this.popEnv = Math.min(1.6, this.popEnv + 0.6 + 0.6 * Math.abs(this.random()));
          amp *= 0.4;
        }
        this.env += amp;
        this.impulse += amp;
        this.nextFire += 1;
      }

      const noise = this.random();
      const blast = this.env * ((1 - p.rasp) + p.rasp * noise);
      this.env *= this.envDecay;
      // Pressure pulses are one-sided; strip their DC before the resonators so
      // dense firing (V12 at load) cannot pin the saturator.
      const rawPulse = blast + this.impulse * 0.6;
      this.impulse = 0;
      const x = rawPulse - this.preX + this.preR * this.preY;
      this.preX = rawPulse;
      this.preY = x;
      const mix = this.body.run(x) * p.bodyGain + this.f1.run(x) * p.formant1Gain + this.f2.run(x) * p.formant2Gain;

      const tapped = this.delay[(this.write - pipeSamples) & this.mask];
      this.fbLow += (tapped - this.fbLow) * (1 - p.pipeDamp);
      const piped = mix + this.fbLow * p.pipeFeedback;
      this.delay[this.write] = piped;
      this.write = (this.write + 1) & this.mask;

      const intake = this.intakeBp.run(noise) * p.intake * load * (0.25 + rpmNorm) * (0.55 + Math.min(0.45, this.env));

      const spoolTarget = load > 0.45 ? Math.min(1, rpmNorm * 1.2) * load : 0;
      this.spool += (spoolTarget - this.spool) * (spoolTarget > this.spool ? 0.00005 : 0.00012);
      let extra = 0;
      if (p.turbo > 0) {
        this.whinePhase += (whineBase * this.pitch) / sr;
        if (this.whinePhase > 1) this.whinePhase -= 1;
        extra += Math.sin(this.whinePhase * TWO_PI) * this.spool * this.spool * p.turbo * 0.05;
        if (this.loadSlow - load > 0.5 && this.spool > 0.35 && this.bov < 0.05) this.bov = this.spool;
        if (this.bov > 0.0005) {
          extra += this.bovHp.run(noise) * this.bov * p.turbo * 0.22;
          this.bov *= 0.99985;
        }
      }
      if (p.supercharger > 0) {
        this.scPhase += (this.rpm / 60) * p.whineRatio * this.pitch / sr;
        if (this.scPhase > 1) this.scPhase -= 1;
        extra += Math.sin(this.scPhase * TWO_PI) * (0.25 + 0.75 * load) * rpmNorm * p.supercharger * 0.035;
      }
      if (this.popEnv > 0.001) {
        extra += this.popLp.run(noise) * this.popEnv * 0.9;
        this.popEnv *= 0.9975;
      }

      const raw = (piped + intake) * p.gain + extra;
      const drive = this.drive * (0.75 + load * 0.5);
      const shaped = Math.tanh(raw * drive) / Math.tanh(drive);
      const y = shaped - this.dcX + 0.996 * this.dcY;
      this.dcX = shaped;
      this.dcY = y;
      out[i] = y * this.level;
    }
    for (let channel = 1; channel < output.length; channel += 1) output[channel].set(out);
    return this.alive;
  }
}

registerProcessor('nightshift-engine', EngineProcessor);
`;
